import { useQuery } from "@tanstack/react-query";
import {
  FilePen,
  Inbox,
  Send,
  FileText,
  Target,
  ClipboardList,
  CalendarDays,
  Sparkles,
  ClipboardCheck,
} from "lucide-react";
import { StatCard } from "@/components/ui/StatCard";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api/client";
import { useAuthStore } from "@/features/auth/store";

type DocMyStats = { created: number; received: number; sent: number; unread: number };
type TextSignMyStats = {
  inbox: number;
  inboxKyNhay: number;
  drafts: number;
  sent: number;
  pendingOthers: number;
  readyPublish: number;
  publishedByMe: number;
};
type TaskStats = { total: number; byStatus: Record<string, number> };
/** Bốn ô "Nhiệm vụ đơn vị" — đọc bảng `mission` theo đúng cách đếm của hệ cũ. */
type MissionStats = {
  assignedByBoard: number;
  coordination: number;
  registered: number;
  upcomingDeadline: number;
};
type UpcomingMeeting = {
  ID: number;
  TITLE: string | null;
  START_TIME: string | null;
  END_TIME: string | null;
  CONTACT: string | null;
};

function useStats() {
  return useQuery({
    queryKey: ["stats", "dashboard"],
    queryFn: async () => {
      const [docMy, task, mission, textSign] = await Promise.all([
        api
          .get("/document/my-stats")
          .then((r) => (r.data.data ?? r.data) as DocMyStats)
          .catch(() => ({ created: 0, received: 0, sent: 0, unread: 0 }) as DocMyStats),
        api
          .get("/task/stats")
          .then((r) => (r.data.data ?? r.data) as TaskStats)
          .catch(() => ({ total: 0, byStatus: {} }) as TaskStats),
        api
          .get("/task/mission-stats")
          .then((r) => (r.data.data ?? r.data) as MissionStats)
          .catch(
            () =>
              ({
                assignedByBoard: 0,
                coordination: 0,
                registered: 0,
                upcomingDeadline: 0,
              }) as MissionStats,
          ),
        api
          .get("/text-sign/my-stats")
          .then((r) => (r.data.data ?? r.data) as TextSignMyStats)
          .catch(
            () =>
              ({
                inbox: 0,
                inboxKyNhay: 0,
                drafts: 0,
                sent: 0,
                pendingOthers: 0,
                readyPublish: 0,
                publishedByMe: 0,
              }) as TextSignMyStats,
          ),
      ]);
      return { docMy, task, mission, textSign };
    },
    staleTime: 60_000,
  });
}

function useUpcomingMeetings() {
  return useQuery({
    queryKey: ["meeting", "upcoming"],
    queryFn: () =>
      api
        .get("/meeting/upcoming")
        .then((r) => (r.data.data ?? r.data) as UpcomingMeeting[])
        .catch(() => [] as UpcomingMeeting[]),
    staleTime: 60_000,
  });
}

function formatMeetingWhen(start: string | null): string {
  if (!start) return "";
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return start;
  return d.toLocaleString("vi-VN", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useStats();
  const { data: meetings = [], isLoading: meetingsLoading } = useUpcomingMeetings();

  const docMy = data?.docMy;
  const textSign = data?.textSign;
  const taskTotal: number = data?.task?.total ?? 0;
  const taskByStatus: Record<string, number> = data?.task?.byStatus ?? {};
  const taskInProgress = (taskByStatus["1"] ?? 0) + (taskByStatus["2"] ?? 0);
  const taskDone = taskByStatus["3"] ?? 0;
  const taskOverdue = taskByStatus["4"] ?? 0;
  const mission = data?.mission;
  const missionTotal =
    (mission?.assignedByBoard ?? 0) +
    (mission?.coordination ?? 0) +
    (mission?.registered ?? 0) +
    (mission?.upcomingDeadline ?? 0);

  const renderNum = (n: number | undefined): string | number => (isLoading ? "…" : (n ?? 0));

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <section className="relative rounded-lg border border-border bg-gradient-to-br from-primary-50 via-background to-accent/10 px-6 py-5 shadow-soft overflow-hidden">
        <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-primary-600" strokeWidth={1.75} />
          <div>
            <div className="font-serif italic text-2xl text-foreground">
              Xin chào, {user?.fullName ?? user?.loginName ?? "bạn"}.
            </div>
            <div className="text-sm text-muted-foreground">
              {user?.orgName ?? "PKHOffice"} · Hôm nay{" "}
              <span className="tabular-nums">
                {new Date().toLocaleDateString("vi-VN", {
                  weekday: "long",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Văn bản nội bộ — OLD home.zul wg-requisitions (3 tiles) */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Văn bản nội bộ
          </h2>
          <div className="hair flex-1 ml-4" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard
            label="Chờ ký duyệt"
            value={renderNum(textSign?.inbox)}
            hint="Số văn bản đang chờ"
            tone="primary"
            icon={FilePen}
            to="/signs/ky-duyet?mode=pending"
          />
          <StatCard
            label="Chờ ký nháy"
            value={renderNum(textSign?.inboxKyNhay)}
            hint="Đang chờ xử lý"
            tone="accent"
            icon={FilePen}
            to="/signs/ky-nhay?mode=pending"
          />
          <StatCard
            label="Chờ ban hành"
            value={renderNum(textSign?.readyPublish)}
            hint="Sẵn sàng ban hành"
            tone="warning"
            icon={Send}
            to="/signs/ban-hanh"
          />
        </div>
      </section>

      {/* Văn bản cá nhân — OLD home.zul wg-documents (4 tiles, per-user) */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Văn bản cá nhân
          </h2>
          <div className="hair flex-1 ml-4" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Văn bản tôi tạo"
            value={renderNum(docMy?.created)}
            tone="primary"
            icon={FilePen}
            to="/documents"
          />
          <StatCard
            label="Văn bản nhận được"
            value={renderNum(docMy?.received)}
            tone="accent"
            icon={Inbox}
            to="/documents/incoming"
          />
          <StatCard
            label="Văn bản chuyển đi"
            value={renderNum(docMy?.sent)}
            tone="muted"
            icon={Send}
            to="/documents/outgoing"
          />
          <StatCard
            label="Văn bản chưa đọc"
            value={renderNum(docMy?.unread)}
            tone="danger"
            icon={FileText}
            to="/documents?unread=1"
          />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Nhiệm vụ */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Nhiệm vụ đơn vị</CardTitle>
            <span className="text-xxs text-muted-foreground uppercase tracking-wider">
              Tổng {missionTotal}
            </span>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="BGĐ giao"
                value={mission?.assignedByBoard ?? 0}
                tone="primary"
                icon={Target}
                to="/tasks"
              />
              <StatCard
                label="Phối hợp"
                value={mission?.coordination ?? 0}
                tone="accent"
                icon={Target}
                to="/tasks"
              />
              <StatCard
                label="Đơn vị đăng ký"
                value={mission?.registered ?? 0}
                tone="success"
                icon={ClipboardList}
                to="/tasks"
              />
              <StatCard
                label="Sắp đến hạn"
                value={mission?.upcomingDeadline ?? 0}
                tone="warning"
                icon={CalendarDays}
                to="/work/summary"
              />
            </div>
            <div className="mt-4 flex items-end h-28 gap-2 pt-3 border-t border-border">
              {Object.entries({
                "BGĐ giao": mission?.assignedByBoard ?? 0,
                "Phối hợp": mission?.coordination ?? 0,
                "Đơn vị đăng ký": mission?.registered ?? 0,
                "Sắp đến hạn": mission?.upcomingDeadline ?? 0,
              }).map(([k, v], i) => {
                const max = Math.max(missionTotal, 1);
                const height = Math.round(((v as number) / max) * 100);
                const colors = [
                  "bg-primary",
                  "bg-accent",
                  "bg-success",
                  "bg-warning",
                  "bg-muted-foreground/60",
                ];
                return (
                  <div key={k} className="flex-1 flex flex-col items-center justify-end gap-1">
                    <div
                      className={`w-full rounded-t-sm transition-all ${colors[i % colors.length]}`}
                      style={{ height: `${height}%` }}
                    />
                    <div className="text-xxs text-muted-foreground tabular-nums">{v as number}</div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>

        {/* Công việc */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Công việc cá nhân</CardTitle>
            <span className="text-xxs text-muted-foreground uppercase tracking-wider">
              Hoàn thành {taskDone}/{taskTotal}
            </span>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-sm text-muted-foreground">Tiến độ tổng</span>
                  <span className="font-serif text-3xl leading-none text-primary-700 tabular-nums">
                    {taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0}
                    <span className="text-xl">%</span>
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-accent transition-all"
                    style={{ width: `${taskTotal > 0 ? (taskDone / taskTotal) * 100 : 0}%` }}
                  />
                </div>
              </div>
              <div className="pt-2 border-t border-border grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="font-serif text-2xl text-foreground tabular-nums">
                    {taskInProgress}
                  </div>
                  <div className="text-xxs uppercase tracking-wider text-muted-foreground mt-1">
                    Đang làm
                  </div>
                </div>
                <div>
                  <div className="font-serif text-2xl text-success tabular-nums">{taskDone}</div>
                  <div className="text-xxs uppercase tracking-wider text-muted-foreground mt-1">
                    Đã xong
                  </div>
                </div>
                <div>
                  <div className="font-serif text-2xl text-destructive tabular-nums">
                    {taskOverdue}
                  </div>
                  <div className="text-xxs uppercase tracking-wider text-muted-foreground mt-1">
                    Quá hạn
                  </div>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Lịch họp — OLD home.zul wg-meetings */}
      <Card>
        <CardHeader>
          <CardTitle>Lịch họp sắp tới</CardTitle>
        </CardHeader>
        <CardBody>
          {meetingsLoading ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              Đang tải lịch họp…
            </div>
          ) : meetings.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-sm">
              Bạn không có lịch họp nào trong 7 ngày tới.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {meetings.slice(0, 5).map((m) => (
                <li key={m.ID} className="py-3 flex items-start gap-3">
                  <ClipboardCheck
                    className="h-4 w-4 text-primary-600 mt-1 shrink-0"
                    strokeWidth={1.75}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground truncate">
                      {m.TITLE ?? "(Không tiêu đề)"}
                    </div>
                    <div className="text-xxs uppercase tracking-wider text-muted-foreground mt-0.5 tabular-nums">
                      {formatMeetingWhen(m.START_TIME)}
                      {m.CONTACT ? ` · ${m.CONTACT}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
